import { EMPTY, fromEvent, merge, Observable, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { fromLayer } from './fromLayer';
import { auditInAnimationFrame } from './rxjs/auditInAnimationFrame';

export interface MouseTrackingController {
  // Called each time a new layer is emitted — for adding styling to the layer for the image resize case.
  onLayer: (layer: Element) => void;
  // Called when the mouse moves on a debounce — for calling controller.onMouseMove.
  onMouseMove: (event: MouseEvent) => void;
  onMouseUp?: (event: MouseEvent) => void;
}

export function fromMouseTracking(
  element: Element,
  document: Document,
  window: Window,
  // Called on mouse down, returns true if we want to start tracking mouse movements
  onMouseDown: (event: MouseEvent) => MouseTrackingController | undefined,
): Observable<void> {
  const controller$ = new Subject<MouseTrackingController | undefined>();
  return merge<void, void>(
    fromEvent<MouseEvent>(element, 'mousedown').pipe(
      tap(event => {
        const newController = onMouseDown(event);
        if (!newController) {
          return;
        }
        controller$.next(newController);

        // Prevent text selection etc.
        event.preventDefault();
        event.stopPropagation();
      }),
    ),
    controller$.pipe(
      switchMap(controller =>
        controller
          ? fromLayer(document).pipe(
              switchMap(layer => {
                controller.onLayer(layer);
                return merge(
                  fromEvent<MouseEvent>(layer, 'mousemove').pipe(
                    auditInAnimationFrame(window),
                    tap(event => controller.onMouseMove(event)),
                  ),
                  merge(
                    fromEvent<MouseEvent>(layer, 'mouseup'),
                    fromEvent<MouseEvent>(layer, 'mouseleave'),
                  ).pipe(
                    tap(event => {
                      try {
                        if (controller.onMouseUp) {
                          controller.onMouseUp(event);
                        }
                      } finally {
                        controller$.next(undefined);
                      }
                    }),
                  ),
                );
              }),
            )
          : EMPTY,
      ),
    ),
  );
}
